import { PipelineStage } from '@/api/queries/pipelines/useGetPipeline';
import useGetPipelineStage from '@/api/queries/pipelines/useGetPipelineStage';
import useDraftAddStageToPipeline from '@/ui/pages/pipelineConfigPage/api/mutations/useDraftAddStageToPipeline';
import useDraftDeletePipelineStage from '@/ui/pages/pipelineConfigPage/api/mutations/useDraftDeletePipelineStage';
import useDraftReorderPipelineStage from '@/ui/pages/pipelineConfigPage/api/mutations/useDraftReorderPipelineStage';
import useDraftUpdatePipelineStage from '@/ui/pages/pipelineConfigPage/api/mutations/useDraftUpdatePipelineStage';
import ColourPicker from '@/ui/pages/pipelineConfigPage/components/stageConfig/ColourPicker';
import PipelineStageConfigDragHandle from '@/ui/pages/pipelineConfigPage/components/stageConfig/PipelineStageConfigDragHandle';
import PipelineStageTitleInput from '@/ui/pages/pipelineConfigPage/components/stageConfig/PipelineStageTitleInput';
import { useMergeRefs } from '@floating-ui/react';
import { Card } from '@payaca/ambrosia';
import { MAX_PIPELINE_STAGES } from '@payaca/constants/pipeline';
import { validateStageReorder } from '@payaca/shared-isomorphic/pipelines/validateStageReorder';
import {
  FC,
  PropsWithChildren,
  RefAttributes,
  useEffect,
  useState,
} from 'react';

type Props = {
  stageId: string;
  stagePublicId: string;
  pipelineId: string;
  title: string;
  colour: string;
  previousStagePublicId?: string;
  nextStagePublicId?: string;
  shouldDisableAddStageActions: boolean;
  stageIndex: number;
  stages: PipelineStage[];
  isNew?: boolean;
} & RefAttributes<HTMLDivElement>;

const PipelineStageConfig: FC<PropsWithChildren<Props>> = (props) => {
  const {
    stageId,
    stagePublicId,
    pipelineId,
    title,
    colour,
    previousStagePublicId,
    nextStagePublicId,
    children,
    shouldDisableAddStageActions,
    stageIndex,
    stages,
    isNew,
    ref: externalRef,
    ...rest
  } = props;

  const { pipelineStage } = useGetPipelineStage({
    pipelineStagePublicId: stagePublicId,
    pipelinePublicId: pipelineId,
  });

  const [cardElement, setCardElement] = useState<HTMLDivElement | null>(null);
  const mergedRef = useMergeRefs([setCardElement, externalRef]);
  useEffect(() => {
    if (isNew) {
      cardElement?.scrollIntoView({
        behavior: 'smooth',
        inline: 'nearest',
      });
    }
  }, [isNew, cardElement]);

  /**
   * mutations - these use the draft QueryClient provided by DraftModeProvider
   */
  const { mutate: updateStage } = useDraftUpdatePipelineStage({
    pipelineId,
  });
  const { mutate: addStage } = useDraftAddStageToPipeline({ pipelineId });
  const { mutate: deleteStage } = useDraftDeletePipelineStage({
    pipelineId,
  });
  const { mutate: reorderStage } = useDraftReorderPipelineStage({
    pipelineId,
  });

  /**
   * derived state
   */
  const moveLeftValidation = previousStagePublicId
    ? validateStageReorder(stages, stageIndex, stageIndex - 1)
    : undefined;

  const moveRightValidation = nextStagePublicId
    ? validateStageReorder(stages, stageIndex, stageIndex + 1)
    : undefined;

  const isDeletable = pipelineStage?.isDeletable;

  return (
    <Card
      ref={mergedRef}
      className="w-80 shrink-0 bg-gray-50 overflow-auto"
      sizeVariant="sm"
      aria-label={`${title} pipeline stage configuration`}
      data-testid="pipeline-stage-config-card"
      {...rest}
    >
      {/*header*/}
      <Card.Body className="flex gap-2 sticky top-0 z-10 bg-gray-50 pb-2.5">
        <PipelineStageConfigDragHandle
          id={stageId}
          items={[
            {
              heading: 'Move',
              items: [
                {
                  icon: 'arrow-left',
                  label: 'Move left',
                  onClick: () => {
                    if (previousStagePublicId) {
                      reorderStage({
                        id: stagePublicId,
                        position: { beforeStageId: previousStagePublicId },
                      });
                    }
                  },
                  disabled:
                    !previousStagePublicId ||
                    moveLeftValidation?.valid === false,
                  disabledReasons:
                    moveLeftValidation?.valid === false
                      ? [moveLeftValidation.reason]
                      : [],
                },
                {
                  icon: 'arrow-right',
                  label: 'Move right',
                  onClick: () => {
                    if (nextStagePublicId) {
                      reorderStage({
                        id: stagePublicId,
                        position: { afterStageId: nextStagePublicId },
                      });
                    }
                  },
                  disabled:
                    !nextStagePublicId || moveRightValidation?.valid === false,
                  disabledReasons:
                    moveRightValidation?.valid === false
                      ? [moveRightValidation.reason]
                      : [],
                },
              ],
            },
            {
              items: [
                {
                  icon: 'arrow-square-left.3',
                  label: 'Add stage left',
                  disabled: shouldDisableAddStageActions,
                  disabledReasons: [
                    `You have reached the maximum number of stages (${MAX_PIPELINE_STAGES})`,
                  ],
                  onClick: () =>
                    addStage({
                      pipelineId,
                      title: 'New Stage',
                      position: { beforeStageId: stagePublicId },
                    }),
                },
                {
                  icon: 'arrow-square-right.3',
                  label: 'Add stage right',
                  disabled: shouldDisableAddStageActions,
                  disabledReasons: [
                    `You have reached the maximum number of stages (${MAX_PIPELINE_STAGES})`,
                  ],
                  onClick: () =>
                    addStage({
                      pipelineId,
                      title: 'New Stage',
                      position: { afterStageId: stagePublicId },
                    }),
                },
                {
                  icon: 'trash-01.3',
                  label: 'Delete stage',
                  disabled: isDeletable?.result === false,
                  disabledReasons: isDeletable?.reasons ?? [],
                  onClick: () => deleteStage({ stageId: stagePublicId }),
                },
              ],
            },
          ]}
        />

        <PipelineStageTitleInput
          stagePublicId={stagePublicId}
          pipelineId={pipelineId}
          title={title}
        />

        <ColourPicker
          value={colour}
          onChange={(value) =>
            updateStage({ id: stagePublicId, colour: value })
          }
        />
      </Card.Body>

      <Card.Body className="pt-0">{children}</Card.Body>
    </Card>
  );
};

export default PipelineStageConfig;
